import React, { useState } from 'react';
import styled from 'styled-components';
import { breakpoints } from '@/components/responsive';

interface QuizOption {
  title: string;
  subtitle: string;
  prompt: string;
}

interface QuizQuestion {
  question: string;
  allowMultiple: boolean;
  options: QuizOption[];
}

interface QuizData {
  quiz: QuizQuestion[];
}

type Answers = Record<number, number[]>;

const extractRawString = (content: unknown): string => {
  if (typeof content === `string`) return content;
  if (Array.isArray(content)) return content.map(extractRawString).join(``);
  return String(content ?? ``);
};

const parseQuizData = (content: unknown): QuizData | null => {
  try {
    return JSON.parse(extractRawString(content));
  } catch {
    return null;
  }
};

const formatAnswersAsMessage = (
  quiz: QuizQuestion[],
  answers: Answers,
): string =>
  quiz
    .map((q, i) => {
      const chosen = (answers[i] ?? []).map(
        (idx) => q.options[idx]?.prompt ?? ``,
      );
      return `**${q.question}**\n${chosen.join(`\n`)}`;
    })
    .join(`\n\n`);

const computeNewSelection = (
  current: number[],
  index: number,
  allowMultiple: boolean,
): number[] => {
  if (!allowMultiple) return [index];
  return current.includes(index)
    ? current.filter((i) => i !== index)
    : [...current, index];
};

interface ProgressDotsProps {
  total: number;
  current: number;
  onPrev: () => void;
  onNext: () => void;
}

function ProgressDots({ total, current, onPrev, onNext }: ProgressDotsProps) {
  return (
    <ProgressBar>
      <NavArrow onClick={onPrev} disabled={current === 0}>
        ‹
      </NavArrow>
      <DotsRow>
        {Array.from({ length: total }).map((_, i) => (
          <StepDot key={i} $active={i === current} />
        ))}
      </DotsRow>
      <NavArrow onClick={onNext} disabled={current >= total - 1}>
        ›
      </NavArrow>
    </ProgressBar>
  );
}

interface CheckboxOptionProps {
  option: QuizOption;
  selected: boolean;
  onToggle: () => void;
}

function CheckboxOption({ option, selected, onToggle }: CheckboxOptionProps) {
  return (
    <OptionRow>
      <CheckInput type="checkbox" checked={selected} onChange={onToggle} />
      <OptionBody>
        <OptionLabel>{option.title}</OptionLabel>
        <OptionHint>{option.subtitle}</OptionHint>
      </OptionBody>
    </OptionRow>
  );
}

interface QuestionStepProps {
  question: QuizQuestion;
  selectedAnswers: number[];
  onToggleOption: (index: number) => void;
  onNext: () => void;
  nextLabel?: string;
}

function QuestionStep({
  question,
  selectedAnswers,
  onToggleOption,
  onNext,
  nextLabel = `Next`,
}: QuestionStepProps) {
  return (
    <StepContainer>
      <QuestionLabel>{question.question}</QuestionLabel>
      {question.allowMultiple && (
        <AllowMultipleNote>* You can select multiple answers</AllowMultipleNote>
      )}
      <OptionsStack>
        {question.options.map((option, i) => (
          <CheckboxOption
            key={i}
            option={option}
            selected={selectedAnswers.includes(i)}
            onToggle={() => onToggleOption(i)}
          />
        ))}
      </OptionsStack>
      <PrimaryButton onClick={onNext} disabled={selectedAnswers.length === 0}>
        {nextLabel}
      </PrimaryButton>
    </StepContainer>
  );
}

interface ReviewCardProps {
  question: QuizQuestion;
  selectedIndices: number[];
  onEdit: () => void;
}

function ReviewCard({ question, selectedIndices, onEdit }: ReviewCardProps) {
  const chosen = selectedIndices
    .map((i) => question.options[i])
    .filter(Boolean);

  return (
    <ReviewCardWrapper>
      <ReviewCardHeader>
        <ReviewCardQuestion>{question.question}</ReviewCardQuestion>
        <InlineEditButton onClick={onEdit}>Edit</InlineEditButton>
      </ReviewCardHeader>
      <ReviewChosenList>
        {chosen.length === 0 ? (
          <SkippedNote>Skipped</SkippedNote>
        ) : (
          chosen.map((opt, j) => (
            <ChosenAnswer key={j}>{opt.title}</ChosenAnswer>
          ))
        )}
      </ReviewChosenList>
    </ReviewCardWrapper>
  );
}

interface ReviewStepProps {
  quiz: QuizQuestion[];
  answers: Answers;
  onEdit: (index: number) => void;
  onSubmit: () => void;
}

function ReviewStep({ quiz, answers, onEdit, onSubmit }: ReviewStepProps) {
  return (
    <StepContainer>
      <QuestionLabel>Review your answers</QuestionLabel>
      {quiz.map((q, i) => (
        <ReviewCard
          key={i}
          question={q}
          selectedIndices={answers[i] ?? []}
          onEdit={() => onEdit(i)}
        />
      ))}
      <PrimaryButton onClick={onSubmit}>Submit</PrimaryButton>
    </StepContainer>
  );
}

interface QuizRendererProps {
  children: unknown;
}

export function QuizRenderer({ children }: QuizRendererProps) {
  const data = parseQuizData(children);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});

  if (!data?.quiz?.length) return null;

  const { quiz } = data;
  const isSingleQuestion = quiz.length === 1;
  const isReview = currentStep >= quiz.length;
  const currentQuestion = quiz[currentStep];

  const toggleOption = (optionIndex: number) => {
    const current = answers[currentStep] ?? [];
    const updated = computeNewSelection(
      current,
      optionIndex,
      currentQuestion.allowMultiple,
    );
    setAnswers((prev) => ({ ...prev, [currentStep]: updated }));
  };

  const goToNext = () =>
    setCurrentStep((prev) => Math.min(prev + 1, quiz.length));
  const goToPrev = () => setCurrentStep((prev) => Math.max(prev - 1, 0));
  const goToQuestion = (index: number) => setCurrentStep(index);

  const handleSubmit = () => {
    window.dispatchEvent(
      new CustomEvent(`ai-chat-input`, {
        detail: formatAnswersAsMessage(quiz, answers),
      }),
    );
  };

  return (
    <QuizContainer>
      {!isReview && !isSingleQuestion && (
        <ProgressDots
          total={quiz.length}
          current={currentStep}
          onPrev={goToPrev}
          onNext={goToNext}
        />
      )}
      {isReview ? (
        <ReviewStep
          quiz={quiz}
          answers={answers}
          onEdit={goToQuestion}
          onSubmit={handleSubmit}
        />
      ) : (
        <QuestionStep
          question={currentQuestion}
          selectedAnswers={answers[currentStep] ?? []}
          onToggleOption={toggleOption}
          onNext={isSingleQuestion ? handleSubmit : goToNext}
          nextLabel={isSingleQuestion ? `Submit` : `Next`}
        />
      )}
    </QuizContainer>
  );
}

const QuizContainer = styled.div`
  background: white;
  padding: 3rem 4rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border-radius: 0.5rem;
  margin: 1rem 0;
  font-family: Eesti, sans-serif;
  flex-wrap: wrap;
  white-space: pre-wrap;

  ${breakpoints.down(`md`)} {
    padding: 2rem;
  }
`;

const ProgressBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
`;

const DotsRow = styled.div`
  display: flex;
  gap: 0.375rem;
`;

const StepDot = styled.span<{ $active: boolean }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $active }) => ($active ? `#111111` : `#d4d4d4`)};
  transition: background 0.2s;
`;

const NavArrow = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #888;
  font-size: 1.375rem;
  line-height: 1;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;

  &:hover:not(:disabled) {
    color: #111;
    background: #f5f5f5;
  }

  &:disabled {
    opacity: 0.25;
    cursor: default;
  }
`;

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 80%;

  ${breakpoints.down(`md`)} {
    max-width: 100%;
  }
`;

const QuestionLabel = styled.p`
  font-size: 2rem;
  line-height: 1.55;
  color: #111;
  margin: 0;
  font-weight: 600;

  ${breakpoints.down(`md`)} {
    font-size: 1.8rem;
  }
`;

const AllowMultipleNote = styled.p`
  font-size: 1.4rem;
  color: #777;
  margin: -0.25rem 0 0;
`;

const OptionsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const OptionRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 2rem;
  cursor: pointer;
  padding: 0.125rem 0;

  &:hover {
    opacity: 0.85;
  }

  ${breakpoints.down(`md`)} {
    gap: 1rem;
  }
`;

const CheckInput = styled.input`
  margin-top: 0.4rem;
  width: 15px;
  height: 15px;
  cursor: pointer;
  flex-shrink: 0;
  accent-color: #111;
`;

const OptionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const OptionLabel = styled.span`
  font-size: 1.6rem;
  font-weight: 500;
  color: #111;
`;

const OptionHint = styled.span`
  font-size: 1.3rem;
  color: #666;
  line-height: 1.45;
  max-width: 80%;

  ${breakpoints.down(`md`)} {
    max-width: 100%;
  }
`;

const PrimaryButton = styled.button`
  max-width: 25rem;
  padding: 1.2rem 1.5rem;
  background: #111111;
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  font-size: 1.4rem;
  font-weight: 600;
  cursor: pointer;
  letter-spacing: 0.01em;
  transition: background 0.2s;
  margin-top: 2rem;

  &:hover:not(:disabled) {
    background: #333;
  }

  &:disabled {
    background: #aaa;
    cursor: not-allowed;
  }

  ${breakpoints.down(`md`)} {
    max-width: 100%;
  }
`;

const ReviewCardWrapper = styled.div`
  padding: 0.75rem 1rem;
`;

const ReviewCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.375rem;
`;

const ReviewCardQuestion = styled.p`
  font-size: 0.82rem;
  font-weight: 500;
  color: #333;
  margin: 0;
  flex: 1;
  line-height: 1.45;
`;

const InlineEditButton = styled.button`
  background: none;
  border: 1px solid #d4d4d4;
  border-radius: 4px;
  color: #666;
  font-size: 0.72rem;
  padding: 0.175rem 0.5rem;
  cursor: pointer;
  flex-shrink: 0;
  transition: border-color 0.15s, color 0.15s;

  &:hover {
    border-color: #111;
    color: #111;
  }
`;

const ReviewChosenList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const ChosenAnswer = styled.span`
  font-size: 0.82rem;
  color: #444;
`;

const SkippedNote = styled.span`
  font-size: 0.82rem;
  color: #aaa;
  font-style: italic;
`;
